import type { InboxEquipmentRow } from '@coffman-tm/shared';
import { Pencil, Plus } from 'lucide-react';
import { InlineNumberInput } from './InlineNumberInput';
import { formatMoneyCents } from '../../lib/format';
import { lineCents } from './blockSubtotals';

export interface EquipmentEdit {
  hours: number;
  rate_cents: number;
}

export interface InboxEquipmentTableProps {
  rows: InboxEquipmentRow[];
  edits: Record<string, EquipmentEdit>;
  editable: boolean;
  onChange: (rowId: string, edit: EquipmentEdit) => void;
  /** Open the Add-to-roster modal for an unknown equipment row. */
  onAddEquipment: (row: InboxEquipmentRow) => void;
  /** Open the Reassign picker — point this line at an existing roster asset. */
  onReassign?: (row: InboxEquipmentRow) => void;
  /** Remove a manually-added equipment line. */
  onRemoveManual?: (row: InboxEquipmentRow) => void;
}

export function InboxEquipmentTable({
  rows,
  edits,
  editable,
  onChange,
  onAddEquipment,
  onReassign,
  onRemoveManual,
}: InboxEquipmentTableProps) {
  if (rows.length === 0) {
    return (
      <div className="muted" style={{ fontSize: 12, padding: '6px 0' }}>
        No equipment on this block.
      </div>
    );
  }
  return (
    <table style={tableStyle}>
      <colgroup>
        <col style={{ width: 100 }} />
        <col />
        <col style={{ width: 110 }} />
        <col style={{ width: 130 }} />
        <col style={{ width: 130 }} />
      </colgroup>
      <thead>
        <tr>
          <th style={thStyle}>Asset ID</th>
          <th style={thStyle}>Description</th>
          <th style={thStyleNum}>Hrs</th>
          <th style={thStyleNum}>Rate</th>
          <th style={thStyleNum}>Total</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const edit = edits[row.daily_equipment_entry_id];
          const hours = edit?.hours ?? 0;
          const rate = edit?.rate_cents ?? 0;
          const total = lineCents(hours, rate);
          return (
            <tr key={row.daily_equipment_entry_id}>
              <td style={tdStyleMono}>{row.asset_id}</td>
              <td style={tdStyle}>
                <span>{row.description}</span>
                {/* Icon row actions — see the matching note in
                    InboxLaborTable; same reasoning, same affordances. */}
                {editable && onReassign && (
                  <button
                    type="button"
                    className="icon-btn sm muted"
                    style={{ marginLeft: 6 }}
                    onClick={() => onReassign(row)}
                    aria-label={`Reassign ${row.description} to a roster machine`}
                    title="Reassign to a roster machine"
                  >
                    <Pencil size={13} aria-hidden />
                  </button>
                )}
                {/* Audit of the line's original identity — for parsed rows
                    that's the foreman's PDF text; for manual rows the raw is
                    a roster snapshot from add time, so no PDF claim. */}
                {!row.is_unknown && row.equipment_name_raw !== row.description && (
                  <span
                    className="muted"
                    style={{ marginLeft: 8, fontSize: 11 }}
                    title={
                      row.is_manual
                        ? 'As originally added to this block'
                        : "As submitted on the foreman's PDF"
                    }
                  >
                    {row.is_manual ? 'Originally:' : 'PDF:'} {row.equipment_name_raw}
                  </span>
                )}
                {row.is_unknown && <UnknownChip />}
                {row.is_unknown && editable && (
                  <button
                    type="button"
                    className="icon-btn sm positive"
                    style={{ marginLeft: 4 }}
                    onClick={() => onAddEquipment(row)}
                    aria-label={`Add ${row.description} to the roster`}
                    title="Add to roster"
                  >
                    <Plus size={14} aria-hidden />
                  </button>
                )}
                {row.is_manual && (
                  <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--accent)', background: 'var(--surface)', border: '1px solid var(--rule)', padding: '1px 6px', borderRadius: 3 }}>
                    manually added
                  </span>
                )}
                {row.is_manual && editable && onRemoveManual && (
                  <button type="button" className="btn-link" style={{ marginLeft: 8, fontSize: 11, color: 'var(--danger)' }} onClick={() => onRemoveManual(row)} title="Remove this manually-added line">
                    Remove
                  </button>
                )}
              </td>
              <td style={tdStyleNum}>
                {editable ? (
                  <InlineNumberInput
                    value={hours}
                    decimals={1}
                    onChange={(v) =>
                      onChange(row.daily_equipment_entry_id, { hours: v, rate_cents: rate })
                    }
                    ariaLabel={`${row.asset_id} hours`}
                  />
                ) : (
                  <span className="mono">{hours.toFixed(1)}</span>
                )}
              </td>
              <td style={tdStyleNum}>
                {editable ? (
                  <InlineNumberInput
                    value={rate / 100}
                    decimals={2}
                    onChange={(v) =>
                      onChange(row.daily_equipment_entry_id, {
                        hours,
                        rate_cents: Math.round(v * 100),
                      })
                    }
                    ariaLabel={`${row.asset_id} rate`}
                  />
                ) : (
                  <span className="mono">{formatMoneyCents(rate, { precise: true })}</span>
                )}
              </td>
              <td style={tdStyleNum}>
                <span className="mono">{formatMoneyCents(total, { precise: true })}</span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function initialEquipmentEdits(
  rows: InboxEquipmentRow[],
): Record<string, EquipmentEdit> {
  const out: Record<string, EquipmentEdit> = {};
  for (const r of rows) {
    out[r.daily_equipment_entry_id] = {
      hours: r.confirmed_hours ?? r.parsed_hours,
      rate_cents: r.confirmed_rate_cents ?? r.default_rate_cents,
    };
  }
  return out;
}

function UnknownChip() {
  return (
    <span
      style={{
        marginLeft: 8,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'var(--warning)',
        background: 'var(--surface-warm)',
        border: '1px solid var(--rule)',
        padding: '1px 6px',
        borderRadius: 3,
      }}
    >
      unknown
    </span>
  );
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '4px 8px',
  borderBottom: '1px solid var(--rule)',
  fontWeight: 600,
  fontSize: 11,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--ink-3)',
};

const thStyleNum: React.CSSProperties = { ...thStyle, textAlign: 'right' };

const tdStyle: React.CSSProperties = {
  padding: '6px 8px',
  borderBottom: '1px solid var(--rule)',
  color: 'var(--ink)',
};

const tdStyleMono: React.CSSProperties = { ...tdStyle, fontFamily: 'var(--mono)', fontSize: 12 };

const tdStyleNum: React.CSSProperties = { ...tdStyle, textAlign: 'right' };
